// Greek text helpers: finding the stressed syllable of each word so it can be marked
// by weight and colour, not only by the accent mark.

const PLAIN_VOWELS = 'αεηιουω';
const ACCENTED_VOWELS = 'άέήίόύώΐΰ';
const DIAERESIS_VOWELS = 'ϊϋΐΰ';
const VOWELS = PLAIN_VOWELS + ACCENTED_VOWELS + 'ϊϋ';
const LETTER = /[\p{Script=Greek}]/u;

/** Two-letter vowel spellings that form one nucleus, when the first letter carries no accent. */
const VOWEL_DIGRAPHS = new Set(['αι', 'ει', 'οι', 'υι', 'ου', 'αυ', 'ευ', 'ηυ']);

/** Consonant clusters that can begin a Greek word, so they start a syllable inside a word too. */
const ONSETS = new Set([
  'βγ', 'βδ', 'βλ', 'βρ', 'γδ', 'γκ', 'γλ', 'γν', 'γρ', 'δρ', 'θλ', 'θν', 'θρ', 'κλ', 'κν', 'κρ', 'κτ',
  'μν', 'μπ', 'ντ', 'πλ', 'πν', 'πρ', 'πτ', 'σβ', 'σγ', 'σθ', 'σκ', 'σλ', 'σμ', 'σν', 'σπ', 'στ', 'σφ',
  'σχ', 'τζ', 'τμ', 'τρ', 'τσ', 'φθ', 'φλ', 'φν', 'φρ', 'φτ', 'χθ', 'χλ', 'χν', 'χρ', 'χτ',
  'στρ', 'σπρ', 'σκρ', 'σπλ', 'σκλ', 'σφρ', 'μπρ', 'μπλ', 'ντρ', 'γκρ', 'γκλ',
]);

const lower = (c: string) => (c === 'ς' ? 'σ' : c.toLowerCase().replace('ς', 'σ'));
const isVowel = (c: string) => VOWELS.includes(c);
const isAccented = (c: string) => ACCENTED_VOWELS.includes(c);

export function isGreekLetter(c: string): boolean {
  return LETTER.test(c);
}

/** Splits one Greek word (letters only) into syllables, returned as [start, end) index pairs. */
export function syllables(word: string): Array<[number, number]> {
  const s = Array.from(word, lower).join('');
  // 1. Find vowel nuclei.
  const nuclei: Array<[number, number]> = [];
  let i = 0;
  while (i < s.length) {
    if (!isVowel(s[i])) {
      i++;
      continue;
    }
    let end = i + 1;
    const pair = s.slice(i, i + 2);
    const second = s[i + 1] ?? '';
    const deaccented = pair.normalize('NFD').replace(/́/g, '').normalize('NFC');
    if (second && !isAccented(s[i]) && !DIAERESIS_VOWELS.includes(second) && VOWEL_DIGRAPHS.has(deaccented)) {
      end = i + 2;
    } else if (
      // Synizesis: an unaccented ι after a consonant glides into the next vowel (μια, πιο, βιά).
      s[i] === 'ι' && i > 0 && !isVowel(s[i - 1]) && second && isVowel(second) && !DIAERESIS_VOWELS.includes(second)
    ) {
      end = i + 2;
    }
    nuclei.push([i, end]);
    i = end;
  }
  if (nuclei.length === 0) return [[0, word.length]];

  // 2. Share the consonants between nuclei.
  const result: Array<[number, number]> = [];
  let start = 0;
  for (let n = 0; n < nuclei.length; n++) {
    const [, nucleusEnd] = nuclei[n];
    const next = nuclei[n + 1];
    if (!next) {
      result.push([start, word.length]);
      break;
    }
    const cluster = s.slice(nucleusEnd, next[0]);
    let onsetLength = cluster.length === 0 ? 0 : 1;
    for (let k = cluster.length; k >= 2; k--) {
      if (ONSETS.has(cluster.slice(cluster.length - k))) {
        onsetLength = k;
        break;
      }
    }
    const boundary = next[0] - onsetLength;
    result.push([start, boundary]);
    start = boundary;
  }
  return result;
}

/** The [start, end) range of the stressed syllable of a word, or null for an unaccented word. */
export function stressedSyllable(word: string): [number, number] | null {
  return stressedSyllables(word)[0] ?? null;
}

/** Every stressed syllable of a word: one, or two when an enclitic adds an accent (διαβατήριό σας). */
export function stressedSyllables(word: string): Array<[number, number]> {
  const s = Array.from(word, lower);
  const accents = s.flatMap((ch, i) => (isAccented(ch) ? [i] : []));
  if (accents.length === 0) return [];
  const all = syllables(word);
  // A one-syllable word (ή, πού) has nothing to single out.
  if (all.length < 2) return [];
  return all.filter(([a, b]) => accents.some((at) => at >= a && at < b));
}

export interface Segment {
  text: string;
  stressed: boolean;
  slot: boolean;
}

/**
 * Splits a phrase into segments for display: the stressed syllable of each word is its own segment,
 * and segments inside the slot (a substring of the phrase) are marked.
 */
export function segmentPhrase(text: string, slot?: string | null): Segment[] {
  const slotStart = slot ? text.indexOf(slot) : -1;
  const slotEnd = slotStart >= 0 ? slotStart + slot!.length : -1;
  const marks = new Array<boolean>(text.length).fill(false);
  const re = /[\p{Script=Greek}]+/gu;
  for (const m of text.matchAll(re)) {
    for (const range of stressedSyllables(m[0])) {
      for (let k = range[0]; k < range[1]; k++) marks[m.index! + k] = true;
    }
  }
  const segments: Segment[] = [];
  for (let k = 0; k < text.length; k++) {
    const stressed = marks[k];
    const inSlot = k >= slotStart && k < slotEnd;
    const last = segments[segments.length - 1];
    if (last && last.stressed === stressed && last.slot === inSlot) last.text += text[k];
    else segments.push({ text: text[k], stressed, slot: inSlot });
  }
  return segments;
}

/** A hint: the first letter of each word, the rest as gaps. «Θα ήθελα» → «Θ_ ή____». */
export function firstLetterHint(text: string): string {
  return text.replace(/[\p{Script=Greek}]+/gu, (w) => w[0] + '_'.repeat(Math.max(0, w.length - 1)));
}

/** A bigger hint: the first half of the words in full, the rest as first letters. */
export function halfHint(text: string): string {
  const words = text.match(/[\p{Script=Greek}]+/gu) ?? [];
  const reveal = Math.ceil(words.length / 2);
  let n = 0;
  return text.replace(/[\p{Script=Greek}]+/gu, (w) => (n++ < reveal ? w : w[0] + '_'.repeat(Math.max(0, w.length - 1))));
}

/** Text compared loosely: lower case, accents kept, punctuation and extra spaces dropped. */
export function looseText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\p{P}\p{S}]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
